<template>
  <cx-toast-card :visible="visible" clickable @click="openConversation" @close="dismiss">
    <div class="cx-toast__body">
      <div class="cx-toast__label">
        <span class="cx-toast__dot" />
        <span class="cx-toast__label-text">{{ label }}</span>
      </div>
      <div class="cx-toast__message">{{ message }}</div>
      <div class="cx-toast__time">{{ time }}</div>
    </div>
  </cx-toast-card>
</template>

<script>
import _ from 'lodash';
import DDLogs from '@/DDLogs';
import Segment from '@/Segment';
import CashflowAssistantApi from '@/api/CashflowAssistant';
import {
  CX_TEAM_LABEL,
  CX_TOAST_ATTACHMENT_MESSAGE,
  CX_TOAST_FALLBACK_MESSAGE,
  MESSAGE_ROLES,
} from '@/constants/chat';
import parseInline from './chat-formatting';
import CxToastCard from './CxToastCard.vue';

// The socket event is a pointer — it carries no message text — so the body is
// read over the authenticated API, same as the chat itself does.
export default {
  name: 'CxMessageToast',
  components: { CxToastCard },
  data() {
    return {
      visible: false,
      surfacedFor: null,
      lastShownMessageId: null,
      conversationId: null,
      label: CX_TEAM_LABEL,
      message: '',
      time: '',
    };
  },
  computed: {
    inlineCxEnabled() {
      return this.$store.getters['featureFlags/riseupChatCxInline'];
    },
    openConversationId() {
      return this.$store.state.riseupChat.openConversationId;
    },
  },
  watch: {
    '$store.state.websocket.chatEvent': function onChatEvent(event) {
      this.handleChatEvent(event);
    },
    // Loaded asynchronously after boot, so the unread state usually arrives
    // after this is already mounted.
    '$store.state.riseupChat.unreadConversationId': function onUnread(conversationId) {
      this.surfaceUnread(conversationId);
    },
    openConversationId(newOpenConversationId) {
      if (this.visible && newOpenConversationId === this.conversationId) {
        this.visible = false;
      }
    },
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.surfaceUnread(this.$store.state.riseupChat.unreadConversationId);
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },
  methods: {
    // The socket stops reconnecting for good after three failed attempts, so a
    // tab that slept through a websockets deploy never hears another push.
    // Returning to the tab reconnects and re-derives unread from the server —
    // the same self-healing the chat itself does on visibilitychange.
    onVisibilityChange() {
      if (document.visibilityState !== 'visible' || !this.inlineCxEnabled) { return; }
      if (this.$socket && !this.$socket.connected) {
        this.$socket.connect();
      }
      this.$store.dispatch('riseupChat/refreshUnread');
    },
    // A reply that arrived while the customer was away never produces a live
    // push, so without this it would only ever be a dot. Surfaced once per
    // conversation: the dot is what persists until it is actually read.
    surfaceUnread(conversationId) {
      if (!conversationId) {
        // Read (or no unread): the next unread conversation may surface again.
        this.surfacedFor = null;
        return;
      }
      if (!this.inlineCxEnabled || conversationId === this.surfacedFor) { return; }
      if (conversationId === this.openConversationId) { return; }
      this.surfacedFor = conversationId;
      this.present(conversationId);
    },
    async handleChatEvent(event) {
      if (!this.inlineCxEnabled || _.get(event, 'type') !== 'cx-message') { return; }
      const conversationId = _.get(event, 'conversationId');
      // Already on screen in the chat itself, so a toast would be noise.
      if (!conversationId || conversationId === this.openConversationId) { return; }
      this.$store.dispatch('riseupChat/refreshUnread');
      await this.present(conversationId);
    },
    // The text is an enhancement, not a precondition: failing to read it must
    // not swallow the notification that the team replied.
    async present(conversationId) {
      const reply = await this.fetchLatestCxReply(conversationId);
      const messageId = _.get(reply, 'messageId');
      if (messageId && messageId === this.lastShownMessageId) { return; }
      if (messageId) { this.lastShownMessageId = messageId; }
      this.conversationId = conversationId;
      this.label = _.get(reply, 'authorName') ? `${reply.authorName} ${CX_TEAM_LABEL}` : CX_TEAM_LABEL;
      this.message = _.get(reply, 'text') || CX_TOAST_FALLBACK_MESSAGE;
      this.time = _.get(reply, 'time') || '';
      this.show();
    },
    async fetchLatestCxReply(conversationId) {
      try {
        const data = await CashflowAssistantApi.getConversationMessages(conversationId);
        const last = _.findLast(data.messages, m => m.role === MESSAGE_ROLES.CX);
        if (!last) { return null; }
        const textBlock = _.find(last.blocks, b => b.type === 'text' && _.trim(b.value));
        return {
          messageId: last.messageId || null,
          authorName: last.authorName || null,
          // An image-only reply has nothing to quote, so it is described instead.
          text: textBlock ? parseInline(textBlock.value).map(part => part.text).join('') : CX_TOAST_ATTACHMENT_MESSAGE,
          time: this.formatTime(last.createdAt),
        };
      } catch (error) {
        DDLogs.error('[inline-cx] Failed to read the reply for the toast', { error });
        return null;
      }
    },
    formatTime(createdAt) {
      if (!createdAt) { return ''; }
      const date = new Date(createdAt);
      if (Number.isNaN(date.getTime())) { return ''; }
      const day = `${date.getDate()}/${date.getMonth() + 1}/${String(date.getFullYear()).slice(-2)}`;
      const clock = `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
      return `${day}, ${clock}`;
    },
    // Stays until dismissed or opened: Figma gives it a close button, and a
    // reply that vanishes on a timer is one the customer can miss entirely.
    show() {
      this.visible = true;
      Segment.trackUserInteraction('AIChatbot_CxReplyToastShown', { conversationId: this.conversationId });
    },
    dismiss() {
      this.visible = false;
      this.$store.dispatch('riseupChat/markRead', this.conversationId);
      Segment.trackUserInteraction('AIChatbot_CxReplyToastDismissed', { conversationId: this.conversationId });
    },
    async openConversation() {
      const { conversationId } = this;
      this.visible = false;
      Segment.trackUserInteraction('AIChatbot_CxReplyToastClicked', { conversationId });
      const { default: openAssistant } = await import('@/chat-boot/assistantLauncher');
      openAssistant({ initiator: 'cx_reply_toast', conversationId });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@riseupil/base-ui/src/scss/riseup-colors';
@import '~@riseupil/base-ui/src/scss/spacings';
@import '~@riseupil/base-ui/src/scss/typography';

/* RTL: flex-start is the RIGHT edge, which is where Figma aligns the label,
   message and timestamp alike. flex-end here would shove short rows left.
   The 40px left padding reserves the close button's column. */
.cx-toast__body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: $ri-spacing-s;
  width: 100%;
  min-width: 0;
  padding: 13px 27px 15px 40px;
  direction: rtl;
}

/* The dot leads the label in reading order, so it sits to its right. */
.cx-toast__label {
  display: flex;
  align-items: center;
  gap: 6px;
}

.cx-toast__label-text {
  font-size: $ri-font-size-14;
  line-height: 20px;
  color: $riseup_black;
  opacity: 0.45;
}

.cx-toast__dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: $riseup_yellow;
  flex-shrink: 0;
}

/* Figma keeps this on one line; real replies are longer, so it clamps. */
.cx-toast__message {
  width: 100%;
  font-size: $ri-font-size-20;
  line-height: 26px;
  color: $riseup_black;
  text-align: right;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.cx-toast__time {
  font-size: $ri-font-size-14;
  line-height: 20px;
  color: $riseup_black;
  opacity: 0.45;
}
</style>
